import React, { useMemo, ReactNode } from "react";
import {
  Grid,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Typography } from "@platform/design-system";
import { CommissionDetail, formatCommission } from "@platform/formatting-utils";
import {
  CLIENT_ACTION_VALUES,
  RFP_ACTION_VALUES,
  TECH_CREDIT_TYPES,
} from "@platform/rfp-core";

import { useRfpContext } from "../contexts";
import {
  RFP_ACTION_TYPES,
  RFP_STEPS,
  DUE_DATE_ALERT_STATUS,
  REQUESTED_SYSTEMS,
} from "../constants";
import { Title, SectionTitle as SecTitle } from "../components";
import { CustomAlert } from "../../../components";
import { scrollToTop, formatDate } from "../../../utils";
import InfoGrid from "../../../components/infoGrid";
import FileCard from "../../../components/fileCard";
import useFormatFiles from "../../../hooks/useFormatFiles";
import { getFilteredCoverages, validateDueDateWhitinDays } from "../utils";

import { CensusCheckSummary } from "./CensusCheckSummary";

const {
  DOCUMENTS,
  SELECTED_CARRIERS,
  COVERAGE_INDEX,
  ADDITIONAL_DOCUMENTS,
  IS_CENTRO_CARRIER_SELECTED,
} = RFP_ACTION_VALUES;
const { UPDATE_BACK_STEP, UPDATE_STEP } = RFP_ACTION_TYPES;
const {
  CLIENT_DETAIL,
  FORM_INFO,
  DUE_DATE,
  EFFECTIVE_DATE,
  HAS_BEN_ADMIN_SYSTEM_STATUS,
  REQUESTED_SYSTEM,
  REQUESTED_SYSTEM_OTHER,
  TECH_IMPLEMENTATION,
  TECH_CREDIT_TYPE,
  TECH_CREDIT_AMOUNT,
  MARKETING_OBJECTIVES,
} = CLIENT_ACTION_VALUES;

const { ERROR, WARNING } = DUE_DATE_ALERT_STATUS;

interface SummaryReviewProps {
  px?: number | string;
}

const DEFAULT_TECH_CREDIT_LABEL = "Tech Credit";

type FormInfo = Record<string, string | undefined>;
type CarrierValue = string | { name: string };

interface ClientDetail {
  situsState?: string;
  producerEmail?: string;
  producerFirstName?: string;
  producerLastName?: string;
}

interface PlanDetail {
  name: string;
  displayName?: string;
  currentCommission?: string;
  requestedCommission?: string;
  currentCommissionDetail?: CommissionDetail;
  requestedCommissionDetail?: CommissionDetail;
}

/** Prefers the normalized detail, falling back to a coverage's free text. */
const commissionLabel = (
  detail?: CommissionDetail,
  freeText?: string,
): string => formatCommission(detail) || freeText || "-";

interface SelectedCoverage {
  name: string;
  details: { plans: PlanDetail[] };
}

// Structurally matches FileCard's (non-exported) file prop type.
interface RfpFile {
  id: string;
  original: { name: string };
  productsByType: Record<string, string[]>;
  infected?: boolean;
  additionalDoc?: boolean;
}

const SummaryReview: React.FC<SummaryReviewProps> = ({ px }) => {
  const { rfp, setRfp, handleSubmitRFP } = useRfpContext();
  const rfpData = rfp as Record<string, unknown>;
  const { coverages: selectedCoverages } = getFilteredCoverages(rfp) as {
    coverages: SelectedCoverage[];
  };
  const selectedCarriers = useMemo<CarrierValue[]>(
    () => (rfpData[SELECTED_CARRIERS] as CarrierValue[]) ?? [],
    [rfpData],
  );
  const formInfo = useMemo<FormInfo>(
    () => (rfpData[FORM_INFO] as FormInfo) ?? {},
    [rfpData],
  );
  const clientDetail = rfpData[CLIENT_DETAIL] as ClientDetail | undefined;
  const centroCarrierSelected = Boolean(rfpData[IS_CENTRO_CARRIER_SELECTED]);
  const additionalDocuments =
    (rfpData[ADDITIONAL_DOCUMENTS] as unknown[]) ?? [];

  const documents = rfpData[DOCUMENTS] as { files: unknown[] };
  const files = useFormatFiles(
    documents.files,
    selectedCoverages,
    additionalDocuments,
  ) as RfpFile[];

  // Format Ben Admin system display
  const benAdminDisplay = useMemo(() => {
    if (formInfo[HAS_BEN_ADMIN_SYSTEM_STATUS] === "unknown") {
      return "I don't know";
    }

    if (formInfo[HAS_BEN_ADMIN_SYSTEM_STATUS] === "0") {
      return "No";
    }

    const systemValue = formInfo[REQUESTED_SYSTEM];
    if (systemValue === "other") {
      return formInfo[REQUESTED_SYSTEM_OTHER] || "Other";
    }

    const system = REQUESTED_SYSTEMS.find((s) => s.value === systemValue);
    return system ? system.label : "N/A";
  }, [formInfo]);

  // Format Tech Credit display
  const {
    techCreditTypeDisplay,
    techCreditValueDisplay,
    techCreditFieldLabel,
  } = useMemo(() => {
    if (formInfo[TECH_IMPLEMENTATION] !== "1") {
      return {
        techCreditTypeDisplay: "No",
        techCreditValueDisplay: "N/A",
        techCreditFieldLabel: DEFAULT_TECH_CREDIT_LABEL,
      };
    }
    const creditType = formInfo[TECH_CREDIT_TYPE];
    const creditAmount = formInfo[TECH_CREDIT_AMOUNT];
    const typeObj = TECH_CREDIT_TYPES.find((t) => t.value === creditType);
    const typeLabel = typeObj ? typeObj.label : creditType;

    if (creditType === "carrierStandard") {
      return {
        techCreditTypeDisplay: typeLabel,
        techCreditValueDisplay: "N/A",
        techCreditFieldLabel: DEFAULT_TECH_CREDIT_LABEL,
      };
    }

    let valueDisplay = "N/A";
    let fieldLabel = DEFAULT_TECH_CREDIT_LABEL;

    if (creditAmount) {
      if (creditType === "percentage") {
        valueDisplay = `${creditAmount}%`;
        fieldLabel = "Tech Credit Percentage";
      } else if (creditType === "dollarAmount") {
        valueDisplay = `$${creditAmount}`;
        fieldLabel = "Tech Credit Dollar Amount";
      } else if (creditType === "pepm") {
        valueDisplay = `$${creditAmount}`;
        fieldLabel = "Tech Credit PEPM";
      }
    }

    return {
      techCreditTypeDisplay: typeLabel,
      techCreditValueDisplay: valueDisplay,
      techCreditFieldLabel: fieldLabel,
    };
  }, [formInfo]);

  const { carriers, coverages } = useMemo(
    () => ({
      carriers: centroCarrierSelected
        ? `Centro to select carriers`
        : selectedCarriers
            .map((c) => (typeof c === "string" ? c : c.name))
            .join(", "),
      coverages: selectedCoverages.flatMap((c, i) =>
        c.details.plans.map((p) => ({
          index: i,
          name: c.name,
          type: p.name,
          displayName: p?.displayName,
          commission: `Current: ${commissionLabel(
            p.currentCommissionDetail,
            p.currentCommission,
          )} — Requested: ${commissionLabel(
            p.requestedCommissionDetail,
            p.requestedCommission,
          )}`,
        })),
      ),
    }),
    [selectedCoverages, selectedCarriers, centroCarrierSelected],
  );

  const handleBack = (): void => {
    setRfp({ type: UPDATE_BACK_STEP });
    scrollToTop();
  };

  const goToStep = (step: number, coverageIndex?: number): void => {
    setRfp({ type: UPDATE_STEP, value: step, [COVERAGE_INDEX]: coverageIndex });
    scrollToTop();
  };

  const dueDate = formInfo[DUE_DATE];

  const dueDateAlertType = useMemo<string | null>(() => {
    if (dueDate) {
      return validateDueDateWhitinDays(dueDate) as string | null;
    }
    return null;
  }, [dueDate]);

  const clientDetailError = useMemo<boolean>(() => {
    if (!clientDetail) return false;
    const requiredFields: (keyof ClientDetail)[] = [
      "situsState",
      "producerEmail",
      "producerFirstName",
      "producerLastName",
    ];
    return requiredFields.some((field) => !clientDetail[field]);
  }, [clientDetail]);

  return (
    <Box width="100%" px={px}>
      <Title title="Review RFP" />

      <Paper sx={{ padding: "2.5rem" }}>
        <Grid container rowGap={4} columnSpacing={0.5}>
          <Grid size={12}>
            <EditSectionTitle onEdit={() => goToStep(RFP_STEPS.RFP_FORM)}>
              RFP Details
            </EditSectionTitle>
          </Grid>
          {!!dueDateAlertType && (
            <Grid size={12}>
              <CustomAlert
                title={
                  dueDateAlertType === WARNING
                    ? "Upcoming Due Date"
                    : "New Due Date Required"
                }
                message={
                  dueDateAlertType === WARNING
                    ? "The provided Due Date is within five business days. Please review and confirm before submitting the RFP."
                    : "The provided Due Date is now past today's date. Please enter a new date to submit your RFP."
                }
                mb={2}
                severity={dueDateAlertType}
              />
            </Grid>
          )}
          {!!clientDetailError && (
            <Grid size={12}>
              <CustomAlert
                title="Client Information Required"
                message="Exit this RFP, navigate to Client Details for this Client, click Edit next to Client Information and complete the missing required fields. You can then return to the RFP draft and click Submit RFP."
                severity="error"
                mb={2}
              />
            </Grid>
          )}
          <Grid size={6}>
            <InfoGrid title="Due Date" value={formatDate(formInfo[DUE_DATE])} />
          </Grid>
          <Grid size={6}>
            <InfoGrid
              title="Effective Date"
              value={formatDate(formInfo[EFFECTIVE_DATE])}
            />
          </Grid>
          {formInfo[TECH_IMPLEMENTATION] === "1" && (
            <>
              <Grid size={6}>
                <InfoGrid
                  title="Tech Credit Type"
                  value={techCreditTypeDisplay}
                />
              </Grid>
              {formInfo[TECH_CREDIT_TYPE] !== "carrierStandard" && (
                <Grid size={6}>
                  <InfoGrid
                    title={techCreditFieldLabel}
                    value={techCreditValueDisplay}
                  />
                </Grid>
              )}
            </>
          )}
          <Grid size={12}>
            <InfoGrid title="Ben Admin System" value={benAdminDisplay} />
          </Grid>
          <Grid size={12}>
            <InfoGrid
              title="Marketing Strategy"
              value={formInfo[MARKETING_OBJECTIVES] || "N/A"}
            />
          </Grid>
          <Grid size={12}>
            <ButtonSectionTitle
              btnText="Add Coverages"
              onClick={() => goToStep(RFP_STEPS.SELECTED_COVERAGES)}
            >
              Quote Coverages
            </ButtonSectionTitle>
            <TableContainer sx={{ mt: 2, px: 2 }}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Coverage</TableCell>
                    <TableCell align="center">Plan Type</TableCell>
                    <TableCell>Commission</TableCell>
                    <TableCell>Edit Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coverages.map((c) => (
                    <TableRow
                      key={`${c.index}-${c.type}`}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {c.name}
                      </TableCell>
                      <TableCell align="center">
                        {c?.displayName || c.type}
                      </TableCell>
                      <TableCell>{c.commission}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() =>
                            goToStep(RFP_STEPS.COVERAGE_FORMS, c.index)
                          }
                        >
                          <EditIcon color="brandPrimary" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid size={12}>
            <EditSectionTitle
              onEdit={() => goToStep(RFP_STEPS.SELECTED_CARRIERS)}
            >
              Carriers
            </EditSectionTitle>
            <InfoGrid
              mt={2}
              px={2}
              value={carriers}
              centroCarrierSelected={centroCarrierSelected}
            />
          </Grid>
          <Grid size={12}>
            <ButtonSectionTitle
              btnText="Edit Documents"
              onClick={() => goToStep(RFP_STEPS.DOCUMENTS)}
            >
              Documents
            </ButtonSectionTitle>
            <Box mt={2}>
              <CensusCheckSummary
                onReview={() => goToStep(RFP_STEPS.DOCUMENTS)}
              />
            </Box>
            <Grid container mt={2} spacing={2}>
              {files.map((file) => (
                <Grid key={file.id} size={6}>
                  <FileCard file={file} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        mt={2}
      >
        <Button
          variant="tertiary"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          data-testid="rfp-submit"
          variant="primary"
          disabled={dueDateAlertType === ERROR || clientDetailError}
          onClick={handleSubmitRFP}
        >
          Submit RFP
        </Button>
      </Stack>
    </Box>
  );
};

interface SectionTitleProps {
  title: string;
  children?: ReactNode;
}

function SectionTitle({
  title,
  children,
}: SectionTitleProps): React.JSX.Element {
  return (
    <SecTitle
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ m: 0 }}
    >
      <Typography variant="header-large" component="h2">
        {title}
      </Typography>
      {children}
    </SecTitle>
  );
}

interface EditSectionTitleProps {
  children: ReactNode;
  onEdit: () => void;
}

function EditSectionTitle({
  children,
  onEdit,
}: EditSectionTitleProps): React.JSX.Element {
  return (
    <SectionTitle title={children as string}>
      <IconButton onClick={onEdit}>
        <EditIcon color="brandPrimary" />
      </IconButton>
    </SectionTitle>
  );
}

interface ButtonSectionTitleProps {
  children: ReactNode;
  btnText: string;
  onClick: () => void;
}

function ButtonSectionTitle({
  children,
  btnText,
  onClick,
}: ButtonSectionTitleProps): React.JSX.Element {
  return (
    <SectionTitle title={children as string}>
      <Button
        variant="secondary"
        sx={{ backgroundColor: "white" }}
        onClick={onClick}
      >
        {btnText}
      </Button>
    </SectionTitle>
  );
}

export default SummaryReview;
